import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { IconButton } from '@material-ui/core';
import { internalTaskRegistry, getInternalTaskState, isAutoApprovedInternalTask } from '@ourbranch/onboarding-tasks';
import { policyTypeNames } from '@ourbranch/policy-types';

import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { capitalize } from 'core/helpers/formatters';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import BaseDialog from 'core/components/base-dialog/base-dialog';

import completedIcon from 'customer/assets/completed.svg';
import pendingIcon from 'core/assets/svg/clock-filled.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import infoIcon from 'core/assets/svg/info-icon-dark.svg';
import documentApprovalIcon from 'core/assets/svg/document-approval.svg';

import useTaskStyles from '../task/task.styles';
import useRevealOnStatusChange from '../task/use-reveal-on-status-change';
import useApproveModalStyles from '../approve-task/approve-task-modal.styles';

const InternalTask = ({
  taskId,
  taskName,
  policyId,
  data,
  bindState,
  completedDate,
  approvalDate,
  approvedBy,
  accountId,
  statusChanged,
  onStatusChange
}) => {
  const classes = useTaskStyles();
  const modalClasses = useApproveModalStyles();
  const toast = useToast();
  const { account } = useStore();

  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const rowRef = useRevealOnStatusChange(statusChanged);

  const config = internalTaskRegistry[taskName];
  const displayName = config?.staffDisplayName || taskName;
  const description = config?.description
    ? config.description({ policyId, data, bindState, completedDate, approvalDate })
    : '';

  const hasPendingPhase = Boolean(config?.hasPendingPhase);
  const state = getInternalTaskState({ completedDate, approvalDate }, config);
  const autoApproved = state === 'approved' && isAutoApprovedInternalTask({ approvedBy });
  const showApprovalPanel = state === 'needsReview';
  const showInlineDescription = hasPendingPhase && !!description && (state === 'pending' || autoApproved);
  const showLearnMore = !hasPendingPhase && !!description;

  // Render the policy-type label off `bindState.policyType` (authoritative
  // single-letter code) rather than parsing the policyId slug. The slug is
  // 'cond' for Condo policies, which would render as 'Cond' through capitalize().
  const policyTypeLabel = policyTypeNames[bindState?.policyType] || capitalize(policyId?.split('-')[1] || '');

  const handleMarkComplete = useCallback(async () => {
    setShowConfirmModal(false);
    setIsApproving(true);
    try {
      const result = await account.approveInternalTask({ taskId, accountId });
      if (result.ok) {
        toast.notify({
          type: 'success',
          message: `The "${displayName}" task has been marked as complete.`
        });
        onStatusChange?.(taskId);
      } else {
        toast.notify({
          type: 'error',
          message: result.error || 'Failed to mark task as complete'
        });
      }
    } finally {
      setIsApproving(false);
    }
  }, [account, taskId, accountId, displayName, toast, onStatusChange]);

  const showSubmittedBackground = state !== 'pending';

  let statusLabel = 'Review pending';
  if (state === 'approved') {
    statusLabel = autoApproved ? 'Auto-approved' : 'Approved';
  }

  return (
    <div
      ref={rowRef}
      className={classNames(classes.container, {
        [classes.completed]: showSubmittedBackground,
        [classes.statusChanged]: statusChanged
      })}
    >
      <div className={classes.task}>
        {approvalDate ? (
          <img alt="approved" className={classes.completedIcon} src={completedIcon} />
        ) : (
          <img alt="review pending" className={classes.pendingIcon} src={pendingIcon} />
        )}
        <div>
          <div className={classNames(classes.taskInfo, classes.taskInfoCompleted)}>
            <div className={classes.taskNameContainer}>
              <Label type="infoValue" className={classes.taskName}>
                {displayName}
              </Label>
              {showLearnMore && (
                <IconButton className={classes.learnMoreLink} onClick={() => setShowLearnMoreModal(true)}>
                  <div className={classes.learnMoreLinkText}>Learn more</div>
                  <img src={arrowIcon} height={14} width={14} alt="Learn More" />
                </IconButton>
              )}
            </div>
          </div>
          <div className={classes.tags}>
            <Tag className={classes.tag}>{policyTypeLabel}</Tag>
            <Tag className={classes.tag}>Internal task</Tag>
            <Tag className={classNames(classes.tag, classes.statusTag)}>
              <img alt="status" src={approvalDate ? completedIcon : pendingIcon} />
              {statusLabel}
            </Tag>
          </div>
          {showInlineDescription && (
            <Label type="smallDarkGreenBold" className={classes.inlineTaskDescription}>
              {description}
            </Label>
          )}
        </div>
      </div>

      {/* Empty right column — no action buttons for internal tasks */}
      <div />

      {showApprovalPanel && (
        <div className={classes.approvalSection} style={{ gap: 16 }}>
          <img src={infoIcon} alt="info" />
          <div className={classNames(classes.column, classes.approvalContent)}>
            {hasPendingPhase ? (
              <>
                <Label type="body3">{description}</Label>
                <Label type="body3">Complete this task once any required manual review has been completed.</Label>
              </>
            ) : (
              <>
                <Label type="body3" fontWeight={600}>
                  This task requires your review.
                </Label>
                <Label type="body3">
                  Complete this task only if the information is correct. If not, handle any required follow-up manually.
                </Label>
              </>
            )}
          </div>
          <div className={classes.column}>
            <Button
              onClick={() => setShowConfirmModal(true)}
              className={classNames(classes.approvalButton, { [classes.approvalButtonLoading]: isApproving })}
              style={{ whiteSpace: 'nowrap' }}
              disabled={isApproving}
            >
              {isApproving ? (
                <CircularProgress size={20} style={{ color: 'white' }} />
              ) : (
                <>
                  <img src={documentApprovalIcon} alt="mark complete" />
                  Mark as complete
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Learn More Modal */}
      <BaseDialog open={showLearnMoreModal} onClose={() => setShowLearnMoreModal(false)}>
        <Label type="greenBig" className={modalClasses.modalHeader}>
          {displayName}
        </Label>
        <Label type="darkGreenSmall" style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {description}
        </Label>
      </BaseDialog>

      {/* Mark as Complete Confirmation Modal */}
      <BaseDialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <Label type="greenBig" className={modalClasses.modalHeader}>
          Mark task as complete?
        </Label>
        <Label type="darkGreenSmall" className={modalClasses.modalContent}>
          Once you mark this task as complete, you won&apos;t be able to change it. Please confirm that all information
          is correct.
        </Label>
        <Grid className={modalClasses.btncontainer}>
          <Button
            variant="contained"
            color="secondary"
            className={modalClasses.nobtn}
            onClick={() => setShowConfirmModal(false)}
          >
            No, go back
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={modalClasses.yesbtn}
            style={{ width: 'auto' }}
            onClick={handleMarkComplete}
          >
            Yes, mark complete
          </Button>
        </Grid>
      </BaseDialog>
    </div>
  );
};

InternalTask.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskName: PropTypes.string.isRequired,
  policyId: PropTypes.string.isRequired,
  data: PropTypes.object,
  bindState: PropTypes.object,
  completedDate: PropTypes.string,
  approvalDate: PropTypes.string,
  approvedBy: PropTypes.string,
  accountId: PropTypes.string.isRequired,
  statusChanged: PropTypes.bool,
  onStatusChange: PropTypes.func
};

export default observer(InternalTask);
